/**
 * Age Verification Component for HoneyDo Platform
 *
 * Verifies user age and routes to appropriate access method:
 * - 18+: Direct registration with no parental consent needed
 * - 13-17: Registration with parental consent required (COPPA compliant)
 * - 8-12: Family PIN login only (parent must have account)
 * - Under 8: Not available
 *
 * COPPA compliant age verification for family platform.
 * Supports multiple access methods based on age group.
 */

import React, { useState } from 'react';
import { Card } from 'flowbite-react';
import EnhancedButton from './ui/EnhancedButton';
import { trackRevenue } from '../services/revenueTracking';

interface AgeVerificationProps {
  onVerificationComplete: (isAdult: boolean, age: number, birthDate: string) => void;
  onBack?: () => void;
}

const AgeVerification: React.FC<AgeVerificationProps> = ({ 
  onVerificationComplete, 
  onBack 
}) => {
  const [birthDate, setBirthDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleVerification = async () => {
    setError(null);

    if (!birthDate) {
      setError('Please enter your birth date');
      return;
    }

    setIsProcessing(true);

    try {
      const age = calculateAge(birthDate);

      // Block under 8 completely
      if (age < 8) {
        setError('HoneyDo is designed for users 8 and older. Please check back when you\'re a bit older!');
        setIsProcessing(false);
        return;
      }

      // Ages 8-12: Show PIN login guidance
      if (age >= 8 && age < 13) {
        setError('Users aged 8-12 can use Family PIN login. Please have a parent create a family account and set up a PIN for you using the "Family Member? Use PIN →" button.');
        setIsProcessing(false);
        return;
      }

      // Ages 13-17 and 18+: Let RegistrationFlow handle routing
      // 13-17 will see "Family Invite Required" screen
      // 18+ will proceed to registration
      const isAdult = age >= 18;

      // Track verification attempt
      trackRevenue({
        event_type: 'onboarding_completed',
        user_id: `temp_${Date.now()}`, // Temporary ID before account creation
        metadata: {
          age_verification: true,
          is_adult: isAdult,
          calculated_age: age
        }
      });

      setTimeout(() => {
        onVerificationComplete(isAdult, age, birthDate);
        setIsProcessing(false);
      }, 1000);

    } catch (error) {
      console.error('Age verification error:', error);
      setError('Verification failed. Please try again.');
      setIsProcessing(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md shadow-2xl border-none">
        <div className="p-8 text-center">
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              🍯 HoneyDo
            </h1>
            <p className="text-gray-600">Family Task Management Platform</p>
          </div>

          {/* Age Verification Form */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Age Verification Required</h2>
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
              Welcome to HoneyDo! Please verify your age to continue. HoneyDo is available for ages 8 and older,
              with multiple family-friendly access methods.
            </p>

            <div className="text-left mb-6">
              <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                id="birthDate"
                value={birthDate}
                onChange={(e) => {
                  setBirthDate(e.target.value);
                  setError(null);
                }}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-medium"
                disabled={isProcessing}
                required
              />
              <div className="text-xs text-gray-500 mt-2">
                Select your birth date to verify your age (must be 8 or older)
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <EnhancedButton
              variant="premium"
              onClick={handleVerification}
              disabled={isProcessing || !birthDate}
              className="w-full mb-4"
              glow
            >
              {isProcessing ? 'Verifying...' : 'Verify Age & Continue'}
            </EnhancedButton>

            {onBack && (
              <EnhancedButton
                variant="secondary"
                onClick={onBack}
                className="w-full"
                disabled={isProcessing}
              >
                Back
              </EnhancedButton>
            )}
          </div>

          {/* Age Groups */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Who Can Use HoneyDo?</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span><strong>Ages 18+:</strong> Full access to all features</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-500 font-bold">✓</span>
                <span><strong>Ages 13-17:</strong> Must be invited by a family adult</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">✓</span>
                <span><strong>Ages 8-12:</strong> Family PIN login only (parent must have account)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-400 font-bold">✗</span>
                <span><strong>Under 8:</strong> Not available at this time</span>
              </div>
            </div>
          </div>

          {/* Family PIN Information */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">👨‍👩‍👧‍👦</span>
              <div className="text-sm text-gray-700">
                <div className="font-semibold text-gray-800 mb-1">Ages 8-12: Family PIN Login</div>
                <div>
                  If you're 8-12 years old and your parent has a HoneyDo account, they can create a Family PIN
                  for you. Use the "Family Member? Use PIN →" button to log in with your PIN instead of signing up.
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Note */}
          <div className="text-xs text-gray-500 leading-relaxed">
            <p>
              🔒 Your birth date is stored securely for age verification and COPPA compliance.
              By continuing, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 font-medium underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 font-medium underline">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-center">
        <p className="text-sm text-gray-500">
          © 2025 HoneyDo • Family Task Management Platform (Ages 13+)
        </p>
      </div>
    </div>
  );
};

export default AgeVerification;